import { FC, ReactNode } from "react";
import { DsmGrid } from "@dsm-dcs/design-system-react";
import { Divider, Typography } from "@material-ui/core";
import { useIntl } from "../../../../../_metronic/i18n/customUseIntl";
import { processAndStageStylesV2 } from "../../../../../_metronic/layout";
import { roiCalculatorStyles } from "../../../../ThemeProvider";
import ReactHookDsmInput from "../../../../modules/Helpers/ReactHookDsmInput2";
import { StageType } from "../../../../../graphql/types";
import { CSSClassesList } from "../../../helpers/helperTypes";
import {
  FARM_GATE_FIELD_MAP,
  ProcessingOutputItem,
  RoiSpecies,
} from "../../../helpers/roiStageData";

type Side = "baseline" | "intervention";

interface PriceAtFarmGateFieldsProps {
  fieldPrefix: string;
  stageType: StageType;
  stageName: string;
  disabled?: boolean;
  species?: RoiSpecies;
  processingOutputs?: {
    baseline: Record<string, ProcessingOutputItem>;
    intervention: Record<string, ProcessingOutputItem>;
  };
  farmGatePrices?: {
    baseline: Record<string, number>;
    intervention: Record<string, number>;
  };
}

interface FarmGateFieldDescriptor {
  formField: string;
  labelId: string;
  placeholderId: string;
}

const LIVE_WEIGHT_PLACEHOLDER =
  "SUSTELL.ROI.FARM_GATE.LIVEWEIGHT_PRICE.PLACEHOLDER";
const EGG_PLACEHOLDER = "SUSTELL.ROI.FARM_GATE.EGG_SELLING_PRICE.PLACEHOLDER";
const DAY_OLD_CHICK_PLACEHOLDER =
  "SUSTELL.ROI.FARM_GATE.DAY_OLD_CHICK_SELLING_PRICE.PLACEHOLDER";

const LIVE_WEIGHT_FIELDS: ReadonlyArray<FarmGateFieldDescriptor> = [
  {
    formField: "liveWeightPrice",
    labelId: "SUSTELL.ROI.FARM_GATE.LIVEWEIGHT_PRICE",
    placeholderId: LIVE_WEIGHT_PLACEHOLDER,
  },
];

const FARM_GATE_FIELDS: Partial<
  Record<StageType, ReadonlyArray<FarmGateFieldDescriptor>>
> = {
  [StageType.Growing]: LIVE_WEIGHT_FIELDS,
  [StageType.Fattening]: LIVE_WEIGHT_FIELDS,
  [StageType.Breeding]: [
    {
      formField: "spentHenRoosterPrice",
      labelId: "SUSTELL.ROI.FARM_GATE.SPENT_HEN_ROOSTER_PRICE",
      placeholderId:
        "SUSTELL.ROI.FARM_GATE.SPENT_HEN_ROOSTER_PRICE.PLACEHOLDER",
    },
    {
      formField: "eggSellingPrice",
      labelId: "SUSTELL.ROI.FARM_GATE.EGG_SELLING_PRICE",
      placeholderId: EGG_PLACEHOLDER,
    },
  ],
  [StageType.Laying]: [
    {
      formField: "consumptionEggPrice",
      labelId: "SUSTELL.ROI.FARM_GATE.CONSUMPTION_EGG_SELLING_PRICE",
      placeholderId: EGG_PLACEHOLDER,
    },
    {
      formField: "spentHen",
      labelId: "SUSTELL.ROI.FARM_GATE.SPENT_HEN",
      placeholderId: LIVE_WEIGHT_PLACEHOLDER,
    },
  ],
  [StageType.Hatching]: [
    {
      formField: "dayOldChickPrice",
      labelId: "SUSTELL.ROI.FARM_GATE.DAY_OLD_CHICK_SELLING_PRICE",
      placeholderId: DAY_OLD_CHICK_PLACEHOLDER,
    },
  ],
};

// PIG (SSTLL-6356): a breeding-only pig farm sells piglets and cull sows at the
// farm gate. Selected over the poultry BREEDING fields when species is PIG.
const PIG_BREEDING_FIELDS: ReadonlyArray<FarmGateFieldDescriptor> = [
  {
    formField: "pigletsSellingPrice",
    labelId: "SUSTELL.ROI.FARM_GATE.PIGLETS_SELLING_PRICE",
    placeholderId: LIVE_WEIGHT_PLACEHOLDER,
  },
  {
    formField: "spentSowSellingPrice",
    labelId: "SUSTELL.ROI.FARM_GATE.SPENT_SOW_SELLING_PRICE",
    placeholderId: LIVE_WEIGHT_PLACEHOLDER,
  },
];

interface ProcessingCategoryDescriptor {
  category: string;
  labelId: string;

  poultryLabelId?: string;
}

const PROCESSING_CATEGORIES: ReadonlyArray<ProcessingCategoryDescriptor> = [
  {
    category: "freshMeatAndEdibleOffal",
    labelId: "SUSTELL.ROI.FARM_GATE.PRODUCT_TYPE.FRESH_MEAT_AND_EDIBLE_OFFAL",
  },
  {
    category: "cat1And2MaterialAndWaste",
    labelId: "SUSTELL.ROI.FARM_GATE.PRODUCT_TYPE.CAT1_AND2_MATERIAL_AND_WASTE",
  },
  {
    category: "cat3SlaughterByProducts",
    labelId: "SUSTELL.ROI.FARM_GATE.PRODUCT_TYPE.CAT3_SLAUGHTER_BY_PRODUCTS",
  },
  {
    category: "cat3HidesAndSkins",
    labelId: "SUSTELL.ROI.FARM_GATE.PRODUCT_TYPE.CAT3_HIDES_AND_SKINS",
    poultryLabelId:
      "SUSTELL.ROI.FARM_GATE.PRODUCT_TYPE.CAT3_FEATHERS_AND_SKINS",
  },
  {
    category: "foodGradeBones",
    labelId: "SUSTELL.ROI.FARM_GATE.PRODUCT_TYPE.FOOD_GRADE_BONES",
  },
  {
    category: "foodGradeFat",
    labelId: "SUSTELL.ROI.FARM_GATE.PRODUCT_TYPE.FOOD_GRADE_FAT",
  },
];

const SIDE_HEADER_ID: Record<Side, string> = {
  baseline: "SUSTELL.ROI.FARM_GATE.BASELINE_STAGE",
  intervention: "SUSTELL.ROI.FARM_GATE.INTERVENTION_STAGE",
};

const STACKED_FIELD_STYLE = { marginTop: "16px" };
const PROCESSING_CELL_STYLE = { marginTop: "8px" };
const PROCESSING_GRID_STYLE = { rowGap: 0 };
const PROCESSING_COLUMN_HEADER_STYLE = { fontWeight: 600, marginBottom: 0 };

const PriceAtFarmGateFields: FC<PriceAtFarmGateFieldsProps> = ({
  fieldPrefix,
  stageType,
  stageName,
  disabled = false,
  species = "POULTRY",
  processingOutputs,
  farmGatePrices,
}) => {
  const intl = useIntl();
  const classes = processAndStageStylesV2() as CSSClassesList;
  const roiClasses = roiCalculatorStyles();

  const isPrefilled = (side: Side, formField: string): boolean => {
    if (!farmGatePrices) {
      return false;
    }

    const pairs = FARM_GATE_FIELD_MAP[stageType];
    const pair = pairs?.find((p) => p.formField === formField);

    if (!pair) {
      return false;
    }

    const value = farmGatePrices[side]?.[pair.outputField];

    return value !== undefined && value !== null;
  };

  const lockedInputProps = (
    side: Side,
    formField: string
  ): { classes: { root: string } } | undefined =>
    isPrefilled(side, formField)
      ? { classes: { root: roiClasses.lockedInput } }
      : undefined;

  const renderSplitColumns = (baseline: ReactNode, intervention: ReactNode) => {
    if (!baseline && !intervention) {
      return null;
    }

    return (
      <div className={roiClasses.splitColumnsContainer}>
        {baseline && <div className={roiClasses.splitColumn}>{baseline}</div>}
        {baseline && intervention && (
          <Divider orientation="vertical" flexItem />
        )}
        {intervention && (
          <div className={roiClasses.splitColumn}>{intervention}</div>
        )}
      </div>
    );
  };

  const renderSideHeader = (side: Side) => (
    <Typography variant="subtitle1" className={roiClasses.stageSubtitle}>
      {intl.formatMessage({ id: SIDE_HEADER_ID[side] }, { stageName })}
    </Typography>
  );

  const renderFieldsSide = (
    side: Side,
    fields: ReadonlyArray<FarmGateFieldDescriptor>
  ) => (
    <>
      {renderSideHeader(side)}

      {fields.map((field, index) => (
        <ReactHookDsmInput
          key={field.formField}
          name={`${fieldPrefix}.${side}Inputs.${field.formField}`}
          label={intl.formatMessage({ id: field.labelId })}
          adornment={intl.formatMessage({ id: field.placeholderId })}
          type="number"
          disabled={disabled || isPrefilled(side, field.formField)}
          InputProps={lockedInputProps(side, field.formField)}
          style={index > 0 ? STACKED_FIELD_STYLE : undefined}
        />
      ))}
    </>
  );

  const renderProcessingSide = (side: Side) => {
    const outputs = processingOutputs?.[side];
    const entries = PROCESSING_CATEGORIES.map((descriptor) => ({
      descriptor,
      item: outputs?.[descriptor.category],
    })).filter(
      (
        entry
      ): entry is {
        descriptor: ProcessingCategoryDescriptor;
        item: ProcessingOutputItem;
      } => entry.item !== undefined
    );

    if (entries.length === 0) {
      return null;
    }

    return (
      <div>
        {renderSideHeader(side)}

        <DsmGrid
          className={classes.dsmGridTwoColumn}
          style={PROCESSING_GRID_STYLE}
        >
          <Typography
            variant="subtitle2"
            style={PROCESSING_COLUMN_HEADER_STYLE}
          >
            {intl.formatMessage({ id: "SUSTELL.ROI.FARM_GATE.WEIGHT" })}
          </Typography>

          <Typography
            variant="subtitle2"
            style={PROCESSING_COLUMN_HEADER_STYLE}
          >
            {intl.formatMessage({ id: "SUSTELL.ROI.FARM_GATE.PRICE" })}
          </Typography>

          {entries.flatMap(({ descriptor, item }) => {
            const { category } = descriptor;
            const label = intl.formatMessage({
              id:
                species === "POULTRY" && descriptor.poultryLabelId
                  ? descriptor.poultryLabelId
                  : descriptor.labelId,
            });

            return [
              <ReactHookDsmInput
                key={`${side}-${category}-w-${item.weight ?? ""}`}
                name={`${fieldPrefix}.processing.${side}.${category}.weight`}
                label={label}
                defaultValue={item.weight ?? ""}
                disabled
                type="number"
                style={PROCESSING_CELL_STYLE}
                showHelperText={false}
                adornment={intl.formatMessage({
                  id: "SUSTELL.ROI.FARM_GATE.WEIGHT.PLACEHOLDER",
                })}
                InputProps={{
                  classes: { root: roiClasses.lockedInput },
                }}
              />,

              <ReactHookDsmInput
                key={`${side}-${category}-p-${item.price ?? ""}`}
                name={`${fieldPrefix}.processing.${side}.${category}.price`}
                label={label}
                defaultValue={item.price ?? ""}
                disabled
                type="number"
                style={PROCESSING_CELL_STYLE}
                showHelperText={false}
                adornment={intl.formatMessage({
                  id: "SUSTELL.ROI.FARM_GATE.PRICE.PLACEHOLDER",
                })}
                InputProps={{
                  classes: { root: roiClasses.lockedInput },
                }}
              />,
            ];
          })}
        </DsmGrid>
      </div>
    );
  };

  if (stageType === StageType.Processing) {
    return renderSplitColumns(
      renderProcessingSide("baseline"),
      renderProcessingSide("intervention")
    );
  }

  const fields =
    stageType === StageType.Breeding && species === "PIG"
      ? PIG_BREEDING_FIELDS
      : FARM_GATE_FIELDS[stageType];
  if (!fields) {
    return null;
  }

  return renderSplitColumns(
    renderFieldsSide("baseline", fields),
    renderFieldsSide("intervention", fields)
  );
};

export default PriceAtFarmGateFields;
